import { Button, Form } from "react-bootstrap";
import { PageConfig } from "../../router";
import { ChangeEvent, useEffect, useState } from "react";
import { Setting } from "../../interfaces/setting.interface";
import { getSetting, updateSetting } from "../../utils/rest-api/settings";
import { toast } from "react-toastify";
import { useApp } from "../../contexts/AppContext";
import MetaComponent from "../../components/common/MetaComponent";

const metadata = {
  title: "Luna - Setting Page",
  description: "Luna - Setting Page",
};

const defaultSetting: Setting = {
  invoiceDuration: undefined,
  transactionFee: undefined,
  maintenanceEnabled: false,
  maintenanceMessage: "",
  minimumVersion: "",
  latestVersion: "",
  forceUpdate: false,
};

export default function SettingPage() {
  const [setting, setSetting] = useState<Setting>(defaultSetting);

  const [invalidSettingProps, setInvalidSettingProps] = useState({
    invoiceDuration: "",
    transactionFee: "",
  });

  const { showLoader, hideLoader } = useApp();

  useEffect(() => {
    showLoader();
    getSetting()
      .then((res) => {
        const d = res.data.data;
        setSetting({
          invoiceDuration: d.invoiceDuration,
          transactionFee: d.transactionFee,
          maintenanceEnabled: d.maintenanceEnabled ?? false,
          maintenanceMessage: d.maintenanceMessage ?? "",
          minimumVersion: d.minimumVersion ?? "",
          latestVersion: d.latestVersion ?? "",
          forceUpdate: d.forceUpdate ?? false,
        });
      })
      .finally(() => hideLoader());
  }, []);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSetting({ ...setting, [e.currentTarget.name]: e.currentTarget.value });
    setInvalidSettingProps({
      ...invalidSettingProps,
      [e.currentTarget.name]: "",
    });
  };

  const handleError = (err: any) => {
    if (err.response?.data) {
      if (typeof err.response.data?.message == "object") {
        setInvalidSettingProps({
          ...invalidSettingProps,
          ...err.response.data.message,
        });
      } else {
        toast.error(err.response.data?.message ?? err.response.data.error);
      }
    } else {
      toast.error("Please check your network connection");
    }
  };

  const onSave = () => {
    updateSetting({
      transactionFee: Number(setting.transactionFee),
      maintenanceEnabled: setting.maintenanceEnabled,
      maintenanceMessage: setting.maintenanceMessage,
      minimumVersion: setting.minimumVersion,
      latestVersion: setting.latestVersion,
      forceUpdate: setting.forceUpdate,
    })
      .then(() => {
        toast.success("Settings successfully updated");
      })
      .catch((e) => {
        handleError(e);
      });
  };

  return (
    <>
      <MetaComponent meta={metadata} />
      <div className="dashboard__main h-100">
        <div className="dashboard__content bg-light-2 h-100 w-100">
          <div className="d-flex flex-column h-100">
            <div className="d-flex flex-row mb-20">
              <span className="text-30 lh-14 fw-600">Setting</span>
            </div>
            <div className="h-100 md-overflow-y-visible overflow-y-scroll">
              <Form
                className="w-form-provider"
                onSubmit={(e) => e.preventDefault()}
              >
                <Form.Group className="mb-40">
                  <Form.Label>Transaction Fee</Form.Label>
                  <Form.Control
                    className={
                      invalidSettingProps.transactionFee && `border-error-2`
                    }
                    type="number"
                    name="transactionFee"
                    value={setting.transactionFee}
                    onChange={onInputChange}
                  />
                  <Form.Text className={`text-error-2`}>
                    {invalidSettingProps.transactionFee}
                  </Form.Text>
                </Form.Group>

                <div className="mb-30">
                  <span className="text-18 lh-14 fw-600">App Management</span>
                </div>

                <div className="mb-20">
                  <span className="text-16 lh-14 fw-500">Maintenance Mode</span>
                </div>

                <Form.Group className="mb-20">
                  <Form.Check
                    type="switch"
                    id="maintenance-enabled"
                    label="Enable maintenance"
                    checked={setting.maintenanceEnabled ?? false}
                    onChange={(e) =>
                      setSetting({ ...setting, maintenanceEnabled: e.currentTarget.checked })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-40">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    type="text"
                    name="maintenanceMessage"
                    placeholder="Maintenance message shown to app users"
                    value={setting.maintenanceMessage ?? ""}
                    onChange={onInputChange}
                  />
                </Form.Group>

                <div className="mb-20">
                  <span className="text-16 lh-14 fw-500">App Version</span>
                </div>

                <Form.Group className="mb-20">
                  <Form.Label>Minimum version</Form.Label>
                  <Form.Control
                    type="text"
                    name="minimumVersion"
                    placeholder="e.g. 1.0.0"
                    value={setting.minimumVersion ?? ""}
                    onChange={onInputChange}
                  />
                </Form.Group>
                <Form.Group className="mb-40">
                  <Form.Label>Latest version</Form.Label>
                  <Form.Control
                    type="text"
                    name="latestVersion"
                    placeholder="e.g. 1.2.3"
                    value={setting.latestVersion ?? ""}
                    onChange={onInputChange}
                  />
                </Form.Group>
                <Form.Group className="mb-40">
                  <Form.Check
                    type="switch"
                    id="force-update"
                    label="Force update"
                    checked={setting.forceUpdate ?? false}
                    onChange={(e) =>
                      setSetting({ ...setting, forceUpdate: e.currentTarget.checked })
                    }
                  />
                </Form.Group>

                <Button onClick={onSave} variant="primary" className="w-100 mb-40">
                  Save
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export const SettingPageConfig: PageConfig = {
  path: "/settings",
  component: SettingPage,
  roles: ["admin", "root"],
};
